import React, { useState, useEffect } from "react";
import { Form, Row, Col, Table } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import calendarIcon from "../../../assets/images/calenderIcon.svg";
import { Calendar } from 'primereact/calendar';
import "./style.css";
import moment from "moment";
import { dateConversion } from "../../../utils/formatDate";

const DateField: React.FC<{
  label: string;
  date: Date | null;
  minDate?: Date;
  disable?: boolean;
  onChange: (date: any) => void;
}> = ({ label, date, onChange, minDate, disable }) => (
  <Col xs={12} md={12}>
    <Form.Group controlId={`fiscal_year_${label.toLowerCase()}`} className="mb-0 d-flex align-items-center">
      <Form.Label className="mb-0 text-right me-3 sm-mb-6">
        {`Fiscal Year ${label} Date:`}
      </Form.Label>
      <div className="pl-3 position-relative">
        <DatePicker
          minDate={minDate}
          selected={date}
          onChange={onChange}
          placeholderText="Select date"
          className="custom-datepicker"
          dateFormat='d MMMM yyyy'
          disabled={disable}
          locale="es"
        />
        <img className="calendar-icon" src={calendarIcon} alt="Calendar Icon" />
      </div>
    </Form.Group>
  </Col>
);

interface FscAafDataProps {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}

const FscAafData: React.FC<FscAafDataProps> = ({ formData, setFormData }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  useEffect(() => {
    if (formData?.FY_Start_Date__c) {
      setStartDate(new Date(formData.FY_Start_Date__c));
    }
    if (formData?.FY_End_Date__c) {
      setEndDate(new Date(formData.FY_End_Date__c));
    }
  }, [formData]);

  const financialTableHeaders = ["", "Forest Products Turnover (FPT)", "Total Revenue (TR)"];

  const financialDataConditions = [
    {description: "--None--", value: null},
    {
      label: "Both FPT and TR are provided AND both are reasonable.",
      description: <span className="fst-italic"> See the description of the approach, rationale, and evidence below.</span>,
      value:"Both Provided & reasonable"
    },
    {
      label: "FPT is provided but TR is not provided (or TR is not reasonable).",
      description: (<span> The <strong>FPT figure is reasonable</strong> and does not involve an element of estimation, meaning there is clear and convincing evidence that it is accurate and has been obtained through a proven reliable process.{" "}
          <span className="fst-italic">See description of approach, rationale, and evidence below</span>
        </span>
      ),
      value:"FPT Provided / TR not (Provided/Reasonable)"
    },
    {
      label: "TR is provided but FPT is not provided (or FPT is not reasonable).",
      description: (
        <span> The <strong>TR figure is reasonable</strong>. The CH understands they will be billed AAF using the Total Revenue,
          according to the FSC Interpretations on AAF Policy.{" "}
          <span className="fst-italic">See the description of approach, rationale, and evidence below.</span>
        </span>
      ),
      value:"FPT not (Provided/Reasonable) / TR Provided"
    },
    {
      label: "FPT and TR are provided BUT both are NOT reasonable:",
      description: (
        <span> Provide a description of the inconsistencies or inaccuracies that led to a conclusion of unreasonableness. The
          default fee option which allowed certificate holders to waive providing TR/FPT data ended on December 31, 2023.
          Beginning January 1, 2024 Minor Non-conformities must be issued if the TR/FPT is not being provided or if TR/FPT
          is unreasonable.{" "}
          <span className="fst-italic">See the description of the approach, rationale, and evidence below.</span>
        </span>
      ),
      value:"FPT/TR Provided & both not reasonable."
    },
    {
      label: "Financial data is NOT provided.",
      description: (
        <span> The default fee option which allowed certificate holders to waive providing TR/FPT data ended on December 31, 2023. Beginning January 1, 2024 Minor Non-conformities must be issued if the TR/FPT is not being provided or if TR/FPT is unreasonable.{" "}
          <span className="fst-italic">See the description of the approach, rationale, and evidence below.</span>
        </span>
      ),
      value:"Financial data is not provided"
    },
  ];


  return (
    <div>
      <div className="fsc-aaf-header">
        <Form.Label className="me-2" style={{ marginBottom: "4px" }}>
          FSC AAF Data
        </Form.Label>
        <Form.Check
          type="checkbox"
          id="na-checkbox"
          label="N/A"
          className="na-checkbox"
          checked={formData?.FSC_AAF_Data__c}
          onChange={(e) => {
            setFormData((prevData: any) => ({
              ...prevData,
              FSC_AAF_Data__c: e.target.checked,
            }));
          }}
        />
      </div>

      <div className="p-4">
        <div className="mb-3 fsc-aaf-text">DATA FOR MOST RECENT COMPLETED FISCAL YEAR</div>

        <Row className="align-items-center">
          <Col xs="auto">
            <Form.Check
              type="checkbox"
              label="N/A, Group Certification (see separate spreadsheet for values)"
              checked={formData?.FSC_Data_Recent_FY__c || false}
              onChange={(e) =>
                setFormData((prevData: any) => ({
                  ...prevData,
                  FSC_Data_Recent_FY__c: e.target.checked,
                }))
              }
            />
          </Col>
        </Row>
        <Row className="my-3">
        <Col md={4}>

          <DateField
            label="Start"
            date={startDate}
            onChange={(date) => {
              setStartDate(date);
              setEndDate(null)
              setFormData((prevData: any) => ({
                ...prevData,
                FY_Start_Date__c: dateConversion(date, "YYYY-MM-DD"),
                FY_End_Date__c: null,
              }));
            }}
          />
          </Col>
          <Col md={4}>
          <DateField
            label="End"
            date={endDate}
            minDate={startDate ? startDate: new Date()}
            disable={!Boolean(startDate)}
            onChange={(date) => {
              setEndDate(date);
              console.log(date)
              setFormData((prevData: any) => ({
                ...prevData,
                FY_End_Date__c: dateConversion(date, "YYYY-MM-DD"),
              }));
            }}
          />
          </Col>
          <Col md={4}>
          <Row className="mb-3">
          <Col xs={12} md={12}>
          <Form.Group className="mb-0 d-flex align-items-center">

                          <Col md={5}>
                            <Form.Label>{"Local Currency Unit:"}</Form.Label>
                          </Col>
                          <Col md={7}>
                            <Form.Control
                              type="text"
                              placeholder={"Local Currency Unit"}
                              value={formData.FPT_Local_Currency_Unit__c || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  FPT_Local_Currency_Unit__c: e.target.value,
                                })
                              }
                            />
                          </Col>
                          </Form.Group>
                          </Col>
                        </Row>
                        </Col>
        </Row>

        <Table bordered className="figures-in-local-currency-table">
          <thead>
            <tr>
              {financialTableHeaders.map((header, index) => (
                <th key={index} className="figures-in-local-currency-table-header">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="figures-in-local-currency-table-data">Figures in Local Currency:</td>
              <td>
                <Form.Control
                  type="text"
                  value={formData?.FPT_Turnover_Local_Currency__c || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      FPT_Turnover_Local_Currency__c: e.target.value,
                    })
                  }
                />
              </td>
              <td>
                <Form.Control
                  type="text"
                  value={formData?.Total_Revenue_Local_Currency__c || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      Total_Revenue_Local_Currency__c: e.target.value,
                    })
                  }
                />
              </td>
            </tr>
            <tr>
              <td className="figures-in-local-currency-table-data">Figures in US Dollars*:</td>
              <td>
                <Form.Control
                  type="text"
                  value={formData?.FPT_Turnover_USD__c || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      FPT_Turnover_USD__c: e.target.value,
                    })
                  }
                />
              </td>
              <td>
                <Form.Control
                  type="text"
                  value={formData?.Total_Revenue_USD__c || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      Total_Revenue_USD__c: e.target.value,
                    })
                  }
                />
              </td>
            </tr>
          </tbody>
        </Table>

        <p className="currency-conversion-note">
          *Convert local currency into US Dollars using the fiscal year end date from the following page:{" "}
          <u>https://www1.oanda.com/currency/converter/</u>
        </p>

        <hr className="divider" />

        <div>
          <p className="fsc-doc-note">
            See FSC's Document Centre for latest <u>AAF Policy</u> and <u>AAF Interpretations</u>.
          </p>

          <p className="fsc-aaf-text">
            Auditor findings when assessing reasonableness of AAF Forest Products Turnover figure.
          </p>
        </div>

        <Row className="mb-3">
          {financialDataConditions.map((data: any, index: number) => (
            <Col xs={12} key={index} style={{ marginBottom: "20px" }}>
              <Form.Check
                type="radio"
                name="financialDataCondition"
                id={`financialDataCondition-${index}`}
                value={data.value ?? "null"} // <-- convert null to string
                label={
                  <>
                    <u className="fsc-aaf-text">{data.label}</u>
                    <span style={{ fontWeight: "400" }}>{data.description}</span>
                  </>
                }
                checked={String(formData?.FPT_TR__c) === String(data.value)}
                onChange={(e) =>
                  setFormData((prevData: any) => ({
                    ...prevData,
                    FPT_TR__c: String(e.target.value) === "null" ? null : e.target.value,
                  }))
                }
              />
            </Col>
          ))}
        </Row>


        <hr className="divider" />

        <div>
          <h5 className="approach-heading">Approach, rationale, and supporting evidence/documentation used to assess reasonableness.</h5>
        </div>

        <div className="content-container">
          <p>
            For the above approach, rationale, and evidence section, ASI and FSC require detailed, specific references to your findings and evidence used regarding reasonableness and consistency of the organization's data.
          </p>
          <p>
            If the total Revenue is left blank, per the allowed circumstances in the Auditor Guidance document, ensure you provide a clear description to support this conclusion.
          </p>
          <p>
            Include key figures and exact references of document names, page numbers, names and titles of interviewees, sections in company software, etc.
          </p>
          <p className="fw-bold">
            If the organization provides different data, be sure to draw attention to this change in the Summary Report “Confirmation of Changes” section as well. This is critical to ensure we are collecting accurate data for FSC. Also inform your Account Manager or affiliate office of this change.
          </p>
          <p>Note the following data requirements for different types of FSC certificates:</p>

          <ul>
            <li className="mb-3">
              <strong className="certificate-heading">Multi-site certificates:</strong> Financial data is aggregated across the participating site(s) included in the scope of certification. Multi-site FPT does not include sites that are not included on the specific certificate you are evaluating.
            </li>
            <li>
              <strong className="certificate-heading">Group certificates:</strong> Financial data is aggregated across all Group Members. Forest Products Turnover and Revenue figures are also separated by Processors and Traders. For some groups, potentially there could be four financial figures to report: USD$ FPT processor, $ FPT trader; $ Revenue processor; $ Revenue trader. These figures are reported on the Participating Sites List, not here. However, you should note your evidence and findings in this section.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FscAafData;
